import React from "react";
import { createPortal } from "react-dom";
import { SVGIcon } from "@pearsontechnology/aero-components-v2";
import type { Media } from "@bronte/viewer";
import type { ViewerDeviceRuntime } from "../../../../../types";
import { NODE_TYPE_POPUPTIP } from "./constants";
import { PopoverWrapper, PopoverContent, PopupTipWrapper, CloseButton } from "./styles";
import { usePopupTip, PopoverStageContent, PopoverBreakOpportunity } from "../common";
import { withInlineVoidElement } from "../../utils";

export default function PopupTip(
  onMediaClick?: (data: Media) => void,
  viewerDevice?: ViewerDeviceRuntime,
) {
  const plugin = (editor: any) => {
    withInlineVoidElement(editor, NODE_TYPE_POPUPTIP);
    return editor;
  };

  plugin.renderElement = ({ element, attributes, children }: any) => {
    if (element.type !== NODE_TYPE_POPUPTIP) {
      return undefined;
    }

    const data = element.data;
    const stage = data?.stage;
    const ref = data?.ref || stage?.ref;
    const dataId = data?.dataId;
    const tipId = ref || dataId;

    return (
      <PopOverComponent
        {...attributes}
        stage={stage}
        tipId={tipId}
        dataKey={dataId}
        onMediaClick={onMediaClick}
        viewerDevice={viewerDevice}
      >
        {children}
      </PopOverComponent>
    );
  };

  return plugin;
}

interface PopOverComponentProps {
  stage: any;
  tipId: string;
  dataKey?: string | number;
  children: React.ReactNode;
  [key: string]: any;
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
}

const PopOverComponent: React.FC<PopOverComponentProps> = ({
  stage,
  tipId,
  dataKey,
  children,
  onMediaClick,
  viewerDevice,
  ...attributes
}) => {
  const {
    showPopupTip,
    hasYOverflow,
    arrowXOffset,
    wrapperRef,
    popoverRef,
    closeButtonRef,
    handleClick,
    handleClose,
    handleKeyDown,
  } = usePopupTip();

  return (
    <>
      <PopupTipWrapper
        {...attributes}
        ref={wrapperRef as React.RefObject<HTMLAnchorElement>}
        href="#"
        data-popup-tip-id={tipId}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-expanded={showPopupTip}
        aria-haspopup="true"
        onKeyDown={handleKeyDown}
      >
        <span>{children}</span>
      </PopupTipWrapper>
      <PopoverBreakOpportunity aria-hidden="true" />
      {showPopupTip &&
        createPortal(
          <PopoverWrapper
            ref={popoverRef}
            id={tipId}
            {...({ type: "popupTip", "data-key": String(dataKey) } as any)}
            className="popover-wrapper popupTip"
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            isOpen={showPopupTip}
            hasYOverflow={hasYOverflow}
            arrowXOffset={arrowXOffset}
          >
            <CloseButton
              onClick={handleClose}
              aria-label="Close"
              className="popover-close-btn"
              type="button"
              tabIndex={0}
              ref={closeButtonRef}
            >
              <SVGIcon icon="closeX" />
            </CloseButton>
            <PopoverContent>
              {stage ? (
                <PopoverStageContent
                  stage={stage}
                  onMediaClick={onMediaClick}
                  viewerDevice={viewerDevice}
                />
              ) : null}
            </PopoverContent>
          </PopoverWrapper>,
          document.getElementById("viewer-express-container") ?? document.body,
        )}
    </>
  );
};
